/**
 * FileUpload — componente reutilizável de upload de arquivos via R2.
 * Drag-and-drop + botão de seleção. Exibe lista de arquivos já enviados.
 */

import { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Image, Film, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { UploadedFile } from '@/lib/types';
import clsx from 'clsx';

interface Props {
  demandaId?: string;
  contratoId?: string;
  readOnly?: boolean;
}

const MAX_SIZE = 25 * 1024 * 1024;

function fileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return Image;
  if (contentType.startsWith('video/')) return Film;
  return FileText;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export default function FileUpload({ demandaId, contratoId, readOnly }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const queryKey = ['uploads', demandaId ?? contratoId];

  const { data: files, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.uploads.list({ demandaId, contratoId }),
    enabled: !!(demandaId || contratoId),
    select: (r) => r.data,
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.uploads.upload(file, { demandaId, contratoId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey });
      setUploadErr(null);
    },
    onError: (err: Error) => {
      setUploadErr(err.message);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (key: string) => api.uploads.delete(key),
    onSuccess: () => qc.invalidateQueries({ queryKey }),
  });

  const handleFiles = useCallback((fileList: FileList) => {
    Array.from(fileList).forEach((f) => {
      if (f.size > MAX_SIZE) {
        setUploadErr(`${f.name}: máximo ${MAX_SIZE / 1024 / 1024}MB`);
        return;
      }
      uploadMut.mutate(f);
    });
  }, [uploadMut]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    handleFiles(e.dataTransfer.files);
  }, [readOnly, handleFiles]);

  const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={clsx(
            'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
            dragOver
              ? 'border-blue bg-blue/10'
              : 'border-border hover:border-blue/50 hover:bg-panel-2',
          )}
        >
          {uploadMut.isPending ? (
            <div className="flex items-center justify-center gap-2 text-xs text-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando...
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5">
              <Upload className="w-5 h-5 text-muted" />
              <span className="text-xs text-muted">
                Arraste arquivos ou <span className="text-blue-light underline">clique aqui</span>
              </span>
              <span className="text-[10px] text-muted/60">Máx 25MB · Imagens, PDFs, vídeos, docs</span>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }}
          />
        </div>
      )}

      {uploadErr && (
        <div className="flex items-center gap-2 text-xs text-red bg-red/10 border border-red/30 rounded px-2 py-1.5">
          <span className="flex-1">{uploadErr}</span>
          <button type="button" onClick={() => setUploadErr(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {/* Lista de arquivos */}
      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted py-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando arquivos...
        </div>
      )}

      {files && files.length > 0 && (
        <div className="space-y-1">
          {files.map((f) => {
            const Icon = fileIcon(f.contentType);
            return (
              <div key={f.key} className="flex items-center gap-2 bg-panel-2 border border-border rounded px-2.5 py-1.5 text-xs group">
                <Icon className="w-3.5 h-3.5 text-muted shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-text-2 font-medium">{f.originalName}</div>
                  <div className="text-[10px] text-muted">
                    {formatSize(f.size)} · {f.uploadedByName || f.uploadedBy}
                  </div>
                </div>
                <a
                  href={`${API_URL}/uploads/${f.key}`}
                  target="_blank"
                  rel="noreferrer"
                  className="opacity-0 group-hover:opacity-100 text-muted hover:text-blue-light transition-opacity"
                  title="Abrir"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => { if (confirm(`Apagar "${f.originalName}"?`)) deleteMut.mutate(f.key); }}
                    className="opacity-0 group-hover:opacity-100 text-muted hover:text-red transition-opacity"
                    disabled={deleteMut.isPending}
                    title="Apagar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
